import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { motion } from 'framer-motion';
import { ArrowRight, ArrowLeft, CheckCircle2, Shield, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ServiceDetail = () => {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchService();
  }, [serviceId]);

  const fetchService = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/services/${serviceId}`);
      setService(response.data);
    } catch (error) {
      console.error('Error fetching service:', error);
      toast.error('Serviciul nu a fost găsit');
      navigate('/servicii');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-cyan-50">
        <Navbar />
        <div className="pt-24 pb-12 px-4 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
        </div>
      </div>
    );
  }

  if (!service) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-cyan-50" data-testid="service-detail-page">
      <Navbar />
      
      {/* Hero Section */}
      <div className="pt-24 pb-8 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Breadcrumb */}
          <nav className="flex items-center space-x-2 text-sm mb-6" data-testid="breadcrumb">
            <Link to="/" className="text-slate-500 hover:text-cyan-600">Acasă</Link>
            <ChevronRight className="w-4 h-4 text-slate-400" />
            <Link to="/servicii" className="text-slate-500 hover:text-cyan-600">Servicii</Link>
            <ChevronRight className="w-4 h-4 text-slate-400" />
            <span className="text-slate-900 font-medium">{service.name}</span>
          </nav>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <h1 
                className="text-4xl lg:text-5xl font-bold text-slate-900 mb-4" 
                style={{ fontFamily: 'Outfit, sans-serif' }}
                data-testid="service-title"
              >
                {service.name}
              </h1>
              
              <div className="flex items-center space-x-4 mb-6">
                <span className="flex items-center bg-orange-100 text-orange-700 px-4 py-2 rounded-full font-bold">
                  <Shield className="w-5 h-5 mr-2" />
                  Garanție {service.warranty}
                </span>
              </div>

              <h2 className="text-xl text-cyan-600 font-semibold mb-4">
                Solicitați acum oferta dvs. fără obligații!
              </h2>
              
              <p className="text-slate-600 text-lg mb-8">
                {service.description}
              </p>

              <Link to={`/vreau-oferta?serviciu=${service.id}`}>
                <Button className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-6 text-lg rounded-full shadow-lg" data-testid="request-quote-btn">
                  Vreau ofertă
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </motion.div>

            {/* Image */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="relative"
            >
              <img
                src={service.image_url}
                alt={service.name}
                className="w-full rounded-2xl shadow-2xl"
                data-testid="service-image"
              />
            </motion.div>
          </div>
        </div>
      </div>

      {/* Phases Section */}
      <div className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <p className="text-cyan-600 font-semibold mb-2">Proces tehnologic pentru realizarea lucrărilor</p>
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Sistem {service.name}
            </h2>
          </motion.div>

          <div className="space-y-6">
            {service.phases?.map((phase, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="flex items-start space-x-6 p-6 bg-slate-50 rounded-xl hover:bg-cyan-50 transition"
                data-testid={`phase-${index}`}
              >
                <div className="flex-shrink-0 w-12 h-12 bg-cyan-600 text-white rounded-full flex items-center justify-center font-bold text-xl">
                  {phase.order}
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {phase.name}
                  </h3>
                  <p className="text-slate-600">
                    {phase.description}
                  </p>
                </div>
                <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0" />
              </motion.div>
            ))}
          </div>

          {/* Warranty Note */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mt-12 p-6 bg-gradient-to-r from-cyan-600 to-cyan-700 rounded-xl text-white text-center"
          >
            <Shield className="w-12 h-12 mx-auto mb-4" />
            <p className="text-lg">
              Sistem garantat <strong>{service.warranty}</strong> integral inclusiv poliță de asigurare asupra lucrărilor de hidroizolații executate.
            </p>
          </motion.div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-16 px-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Solicită ofertă pentru acest serviciu
          </h2>
          <p className="text-xl text-orange-100 mb-8">
            Completează formularul și primești oferta în maxim 24 de ore!
          </p>
          <div className="flex justify-center gap-4">
            <Link to="/servicii">
              <Button variant="outline" className="bg-transparent border-white text-white hover:bg-white/10 px-6 py-4">
                <ArrowLeft className="mr-2 w-4 h-4" />
                Înapoi la Servicii
              </Button>
            </Link>
            <Link to={`/vreau-oferta?serviciu=${service.id}`}>
              <Button className="bg-white text-orange-600 hover:bg-orange-50 px-8 py-4 text-lg rounded-full shadow-lg">
                VREAU OFERTĂ
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceDetail;
